const STATUS_META = {
  0: { label: "未知状态", color: "#616161", backgroundColor: "#f5f5f5" },
  1: { label: "入库", color: "#2e7d32", backgroundColor: "#e8f5e9" },
  2: { label: "调仓", color: "#00838f", backgroundColor: "#e0f7fa" },
  3: { label: "出货", color: "#1565c0", backgroundColor: "#e3f2fd" },
  4: { label: "调拨", color: "#6a1b9a", backgroundColor: "#f3e5f5" },
  5: { label: "零售", color: "#ef6c00", backgroundColor: "#fff3e0" },
  6: { label: "退货", color: "#c62828", backgroundColor: "#ffebee" },
  7: { label: "零售退货", color: "#ad1457", backgroundColor: "#fce4ec" },
};

const BASE_STATUS_LABELS = {
  0: "未入库",
  1: "已入库",
  2: "已发货",
  3: "已出货",
  4: "已调拨",
  5: "已零售",
  6: "已退货",
  7: "已零售退货",
};

export function getStatusMeta(status) {
  return STATUS_META[Number(status)] || STATUS_META[0];
}

export function getBaseStatusText(baseData) {
  if (!baseData || baseData.status === undefined || baseData.status === null) {
    return "--";
  }

  const status = Number(baseData.status);
  let label = BASE_STATUS_LABELS[status] || "未知状态";

  if (status >= 0 && status <= 5 && Number(baseData.is_devanning) === 1) {
    const notes = ["拆箱"];
    if (Number(baseData.inwarehouse_status) === 1) {
      notes.push("未入库");
    }
    label += `(${notes.join("、")})`;
  }

  if ((status === 6 || status === 7) && Number(baseData.is_verify_status) !== 0) {
    label += Number(baseData.is_verify_status) === 2 ? "(审核失败)" : "(未审核)";
  }

  return label;
}

export function filterLogisticsItems(items, configInfo) {
  const list = Array.isArray(items) ? items : [];
  const warehousingConfirm = Number(
    configInfo && configInfo.warehousing_confirm
  );

  if (warehousingConfirm !== 0) {
    return list;
  }

  return list.filter((item) => Number(item && item.is_tmp) !== 1);
}

export function normalizeLogisticsCode(value) {
  let result = String(value || "").trim();

  for (let index = 0; index < 2; index += 1) {
    try {
      const decoded = decodeURIComponent(result);
      if (decoded === result) {
        break;
      }
      result = decoded;
    } catch (error) {
      break;
    }
  }

  return result;
}

function getCodeLevel(item, fallbackLevel) {
  const value =
    item && item.code_type !== undefined
      ? item.code_type
      : item && item.level !== undefined
      ? item.level
      : fallbackLevel;
  const level = Number(value);
  return level >= 0 && level <= 2 ? level : fallbackLevel;
}

function getCodeValue(item, level) {
  if (!item) {
    return "";
  }

  return (
    item.code ||
    item.logistics_code ||
    item[["big_code", "middle_code", "small_code"][level]] ||
    item.big_code ||
    item.middle_code ||
    item.small_code ||
    ""
  );
}

function normalizeTreeNodes(nodes, fallbackLevel, parentKey, inheritedStackCode) {
  return (Array.isArray(nodes) ? nodes : []).reduce((result, item, index) => {
    if (!item || typeof item !== "object") {
      return result;
    }

    const level = getCodeLevel(item, fallbackLevel);
    const code = getCodeValue(item, level);
    const stackCode = item.stack_code || inheritedStackCode || null;
    const key = `${parentKey}-${index}-${String(code || "node")}`;
    const children = normalizeTreeNodes(
      item.children,
      Math.min(level + 1, 2),
      key,
      stackCode
    );

    if (!code && children.length) {
      result.push(...children);
      return result;
    }

    if (!code) {
      return result;
    }

    const node = Object.assign({}, item, {
      code: String(code),
      key,
      level,
      stack_code: stackCode,
      children,
    });
    result.push(node);
    return result;
  }, []);
}

function normalizeLegacyCodeRows(rows) {
  const roots = [];

  rows.forEach((row) => {
    if (!row || typeof row !== "object") {
      return;
    }

    const levels = [row.big_code, row.middle_code, row.small_code]
      .map((code, level) => ({ code, level }))
      .filter(
        (item) =>
          item.code !== undefined && item.code !== null && item.code !== ""
      );
    let siblings = roots;
    let parentKey = "legacy";
    let leaf = null;

    levels.forEach((item) => {
      const code = String(item.code);
      let node = siblings.find(
        (candidate) => candidate.code === code && candidate.level === item.level
      );

      if (!node) {
        const key = `${parentKey}-${item.level}-${code}`;
        node = {
          code,
          key,
          level: item.level,
          logistics_code: code,
          stack_code: item.level === 0 ? row.stack_code || null : null,
          children: [],
        };
        siblings.push(node);
      }

      if (item.level === 0 && row.stack_code) {
        node.stack_code = row.stack_code;
      }
      leaf = node;
      parentKey = node.key;
      siblings = node.children;
    });

    if (leaf) {
      leaf.serialnum = row.serialnum || leaf.serialnum || "";
      leaf.updated_at = row.updated_at || leaf.updated_at || "";
      leaf.logistics_code = row.logistics_code || leaf.logistics_code;
    }
  });

  return roots;
}

export function normalizeCodeTree(codeList) {
  const list = Array.isArray(codeList) ? codeList : [];
  const treeRows = list.filter(
    (item) => item && Object.prototype.hasOwnProperty.call(item, "children")
  );
  const legacyRows = list.filter(
    (item) => item && !Object.prototype.hasOwnProperty.call(item, "children")
  );

  return [
    ...normalizeTreeNodes(treeRows, 0, "root", null),
    ...normalizeLegacyCodeRows(legacyRows),
  ];
}

export function countCodeTree(codeTree) {
  const counts = [0, 0, 0];
  const walk = (nodes) => {
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      const level = Number(node && node.level);
      if (level >= 0 && level <= 2) {
        counts[level] += 1;
      }
      walk(node && node.children);
    });
  };

  walk(codeTree);
  return counts;
}

export function findCodeTreePath(codeTree, targetCode) {
  const target = normalizeLogisticsCode(targetCode);
  if (!target) {
    return [];
  }

  const walk = (nodes, parentPath) => {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      const path = parentPath.concat(node.key);
      const values = [
        node.code,
        node.logistics_code,
        node.big_code,
        node.middle_code,
        node.small_code,
      ]
        .filter((value) => value !== undefined && value !== null)
        .map(normalizeLogisticsCode);

      if (values.includes(target)) {
        return path;
      }

      const childPath = walk(node.children, path);
      if (childPath.length) {
        return childPath;
      }
    }

    return [];
  };

  return walk(codeTree, []);
}

export function normalizeFlowItem(item) {
  const source = item || {};
  const status = Number(source.status);
  const toName = source.to_name || "";
  const toSn = source.to_sn || "";
  const warehouse = String(
    source.warehouse_name || source.from_warehouse_name || ""
  ).trim();
  let merchant = "--";
  let merchantLabel = "出库商户";
  let orderNo = "--";

  if (status === 1) {
    merchant = source.operator || "--";
  } else if (toSn && toName) {
    merchant = `(${toSn})-${toName}`;
  } else if (toSn) {
    merchant = `(${toSn})`;
  } else if (toName) {
    merchant = toName;
  }

  if (status === 1) {
    merchantLabel = "入库商户";
  } else if (status === 4) {
    merchantLabel = "调入商户";
  } else if (status === 6 || status === 7) {
    merchantLabel = "退回商户";
  }

  if (status === 1 || status === 2) {
    orderNo = source.stock_in_sn || "--";
  } else if (status >= 3 && status <= 5) {
    orderNo = source.stock_out_sn || "--";
  } else if (status === 6 || status === 7) {
    orderNo = source.return_goods_sn || "--";
  }

  return {
    status,
    statusLabel: getStatusMeta(status).label,
    orderNo,
    merchant,
    merchantLabel,
    createdAt: source.created_at || "--",
    warehouse,
    operator: source.operator || "--",
  };
}

export function flattenCodeTree(codeList) {
  const nodes = Array.isArray(codeList) ? codeList : [];
  const isTree = nodes.some((node) => node && Array.isArray(node.children));

  if (!isTree) {
    return nodes.map((node) => Object.assign({}, node));
  }

  const rows = [];
  const walk = (node, parentCodes, inheritedStackCode) => {
    if (!node) {
      return;
    }

    const codes = Object.assign(
      { big_code: "", middle_code: "", small_code: "" },
      parentCodes
    );
    const codeType = Number(node.code_type);
    const logisticsCode = node.logistics_code || "";

    if (codeType === 0) {
      codes.big_code = node.big_code || logisticsCode;
    } else if (codeType === 1) {
      codes.middle_code = node.middle_code || logisticsCode;
    } else if (codeType === 2) {
      codes.small_code = node.small_code || logisticsCode;
    }

    const stackCode = node.stack_code || inheritedStackCode || null;
    const children = Array.isArray(node.children) ? node.children : [];

    if (children.length) {
      children.forEach((child) => walk(child, codes, stackCode));
      return;
    }

    const row = Object.assign({}, node, codes, { stack_code: stackCode });
    delete row.children;
    rows.push(row);
  };

  nodes.forEach((node) => walk(node, {}, null));
  return rows;
}
